/**
 * sb-cookie.js — SoundBird cookie consent
 * ---------------------------------------------------------------------------
 * SoundBird's own banner and preferences modal, storing consent through
 * Shopify's Customer Privacy API. The API is what actually gates the scripts
 * per category, so nothing here rolls its own cookie or its own gating — this
 * file is the UI and the decision, Shopify enforces it.
 *
 * Vanilla, no dependencies.
 */
(() => {
  const banner = document.querySelector('[data-sb-cookie]');
  const prefs = document.querySelector('[data-sb-cookie-prefs]');
  if (!banner || !prefs) return;

  const CATS = ['preferences', 'marketing', 'analytics'];
  const input = (cat) => prefs.querySelector(`[data-sb-cat="${cat}"]`);

  let api = null;
  let lastFocus = null;

  /* ── Shopify's API ──────────────────────────────────────────────────
     loadFeatures is async and may already have run, so this resolves either
     way rather than assuming a load order. */
  function withApi() {
    return new Promise((resolve) => {
      if (window.Shopify?.customerPrivacy) return resolve(window.Shopify.customerPrivacy);
      if (!window.Shopify?.loadFeatures) return resolve(null);
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
        resolve(err ? null : window.Shopify.customerPrivacy || null);
      });
    });
  }

  function showBanner() {
    banner.hidden = false;
    /* Two frames: `hidden` removal has to be painted before the transition
       start value exists, or the card appears instead of rising. */
    requestAnimationFrame(() => requestAnimationFrame(() => banner.setAttribute('data-on', '')));
  }

  function hideBanner() {
    banner.removeAttribute('data-on');
    const done = () => { banner.hidden = true; banner.removeEventListener('transitionend', done); };
    banner.addEventListener('transitionend', done);
    /* Belt and braces — if the transition never fires (reduced motion, a
       backgrounded tab), the card must still end up hidden. */
    setTimeout(done, 500);
  }

  /**
   * @param {{preferences:boolean, marketing:boolean, analytics:boolean}} choice
   */
  function persist(choice) {
    if (!api) return;
    api.setTrackingConsent(
      {
        preferences: choice.preferences,
        analytics: choice.analytics,
        marketing: choice.marketing,
        /* Sale-of-data tracks marketing: it is the category that covers sharing
           with advertising partners, which is what the banner's copy describes. */
        sale_of_data: choice.marketing,
      },
      (result) => {
        /* The write goes to Shopify's storefront API, and it FAILS on the local
           dev server — "Failed to fetch", because the request is cross-origin
           from localhost. It is not a signal about the call shape: the API
           accepts the payload and attempts the write. Surfaced rather than
           swallowed so a real failure in production is visible in the console
           instead of looking like the banner simply forgot the answer.

           Deliberately does NOT re-open the banner. The visitor answered; if
           the answer could not be stored they will be asked again next load,
           which is the safe outcome — no consent is assumed either way. */
        if (result?.error) console.warn('[sb-cookie] consent not stored:', result.error);
      }
    );
  }

  const all = () => ({ preferences: true, marketing: true, analytics: true });
  const none = () => ({ preferences: false, marketing: false, analytics: false });
  const fromSwitches = () =>
    CATS.reduce((acc, c) => { acc[c] = !!input(c)?.checked; return acc; }, {});

  /* ── Remembering the answer ─────────────────────────────────────────
     Shopify's own store is the source of truth for whether tracking is
     ALLOWED. This is a separate, smaller record of whether the visitor has
     been ASKED — and the two are not the same thing.

     setTrackingConsent can fail (it always does on the dev server, where the
     write is cross-origin from localhost: "Failed to fetch"). When it did,
     nothing was recorded anywhere, shouldShowBanner() stayed true, and the
     banner asked again on every single page (owner 2026-08-04). Being asked
     the same question forever is its own kind of broken.

     So the choice is kept locally as well, and on the next load it is REPLAYED
     into setTrackingConsent rather than merely suppressing the banner. That
     way a write that failed once still lands later, and consent ends up where
     it belongs instead of being quietly approximated in localStorage.

     Wrapped because storage throws outright in some privacy modes; the
     fallback is simply the old behaviour. */
  const STORE_KEY = 'sb-cookie-choice';

  function remember(choice) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(choice)); } catch { /* no store */ }
  }

  function recalled() {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      const c = JSON.parse(raw);
      return CATS.every((k) => typeof c?.[k] === 'boolean') ? c : null;
    } catch { return null; }
  }

  function answer(choice) {
    remember(choice);
    persist(choice);
    closePrefs();
    hideBanner();
  }

  /* ── Preferences modal ──────────────────────────────────────────────
     A real dialog: it traps focus and returns it, because the visitor opened it
     deliberately. The banner itself never traps — it must not block the page. */
  function openPrefs() {
    lastFocus = document.activeElement;
    /* Seed the switches from whatever consent currently stands, so the modal
       shows the truth rather than a default. */
    const current = api?.currentVisitorConsent?.() || {};
    CATS.forEach((c) => {
      const el = input(c);
      if (el) el.checked = current[c] === true || current[c] === 'yes';
    });
    prefs.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      prefs.setAttribute('data-on', '');
      prefs.querySelector('[data-sb-cookie-all]')?.focus({ preventScroll: true });
    }));
  }

  function closePrefs() {
    if (prefs.hidden) return;
    prefs.removeAttribute('data-on');
    const done = () => { prefs.hidden = true; prefs.removeEventListener('transitionend', done); };
    prefs.addEventListener('transitionend', done);
    setTimeout(done, 500);
    if (lastFocus?.focus) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-sb-cookie-accept]')) return answer(all());
    if (e.target.closest('[data-sb-cookie-decline]')) return answer(none());
    if (e.target.closest('[data-sb-cookie-all]')) return answer(all());
    if (e.target.closest('[data-sb-cookie-none]')) return answer(none());
    if (e.target.closest('[data-sb-cookie-save]')) return answer(fromSwitches());
    if (e.target.closest('[data-sb-cookie-manage]')) return openPrefs();
    if (e.target.closest('[data-sb-cookie-close]')) return closePrefs();
    /* Clicking the backdrop closes without answering — the banner stays up, so
       no choice is silently recorded. */
    if (e.target === prefs) closePrefs();
  });

  document.addEventListener('keydown', (e) => {
    if (prefs.hidden) return;
    if (e.key === 'Escape') { e.preventDefault(); return closePrefs(); }
    if (e.key !== 'Tab') return;
    const focusables = [...prefs.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    )].filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ── Boot ───────────────────────────────────────────────────────────
     Shopify decides whether a banner is due — it knows the visitor's region and
     whether they have already answered. If the API is unavailable the banner
     stays down rather than showing something that cannot record an answer. */
  withApi().then((cp) => {
    api = cp;
    if (!api) return;

    let due = false;
    try {
      due = api.shouldShowBanner();
    } catch {
      /* shouldShowBanner throws if the API loaded without a shop config. */
      return;
    }
    if (!due) return;

    /* Shopify says to ask — but if the visitor has already answered and only
       the write failed, replay the answer instead of asking again. */
    const previous = recalled();
    if (previous) { persist(previous); return; }

    showBanner();
  });

  /* Re-openable from anywhere — a footer link, the privacy policy page.
     Placement is still the owner's call (handoff: "ask me for placement"). */
  window.SBCookie = {
    openPreferences: openPrefs,
    close: closePrefs,
    consent: () => api?.currentVisitorConsent?.() || null,
  };
})();
